import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  OpenImageViewerShell,
  type OpenImageViewerActionContext,
  type OpenImageViewerConfig,
} from '@/features/open-image-viewer'
import { useT } from '@/shared/i18n'
import {
  exportWebpImage,
  loadWebpImage,
  validateWebp,
  type WebpExportFormat,
} from './model/webpViewer'

const UTILITY_KEY = 'webp-viewer'

function WebpExportActions({ context }: { context: OpenImageViewerActionContext }) {
  const t = useT()
  const [exporting, setExporting] = useState<WebpExportFormat | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  const exportGenerationRef = useRef(0)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      exportGenerationRef.current += 1
    }
  }, [])

  const exportImage = useCallback(
    async (format: WebpExportFormat) => {
      const exportGeneration = ++exportGenerationRef.current
      const isActive = () =>
        mountedRef.current && exportGeneration === exportGenerationRef.current
      setExporting(format)
      setExportError(null)
      const params = { action: 'export', format }
      toolInvoke(UTILITY_KEY, params)
      const startedAt = performance.now()

      try {
        if (!context.image.element) throw new Error(t.webpViewer.exportError)
        const completed = await exportWebpImage({
          element: context.image.element,
          width: context.image.width,
          height: context.image.height,
          filename: context.file.name,
          format,
          isActive,
          onSuccess: () =>
            toolSuccess(
              UTILITY_KEY,
              Math.round(performance.now() - startedAt),
              params,
            ),
        })
        if (!completed || !isActive()) return
      } catch {
        if (!isActive()) return
        toolError(UTILITY_KEY, 'export_failed', params)
        setExportError(t.webpViewer.exportError)
      } finally {
        if (isActive()) setExporting(null)
      }
    },
    [context, t.webpViewer.exportError],
  )

  return (
    <div className="space-y-3">
      <div className="grid gap-2">
        <Button
          type="button"
          disabled={exporting !== null}
          onClick={() => void exportImage('jpg')}
        >
          {exporting === 'jpg'
            ? t.webpViewer.exportingJpg
            : t.webpViewer.downloadJpg}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={exporting !== null}
          onClick={() => void exportImage('png')}
        >
          {exporting === 'png'
            ? t.webpViewer.exportingPng
            : t.webpViewer.downloadPng}
        </Button>
      </div>
      <p className="text-xs leading-5 text-muted-foreground">
        {t.webpViewer.staticFrameNote}
      </p>
      {exportError && (
        <p className="text-sm leading-6 text-destructive">{exportError}</p>
      )}
    </div>
  )
}

export function WebpViewerPage() {
  const t = useT()
  const config: OpenImageViewerConfig = {
    analyticsKey: UTILITY_KEY,
    accept: '.webp,image/webp',
    acceptedMimeTypes: ['image/webp'],
    acceptedExtensions: ['.webp'],
    maxFileSizeBytes: 50 * 1024 * 1024,
    maxPixels: 36_000_000,
    formatLabel: 'WebP',
    previewBackground: 'checkerboard',
    validateFile: (file, signal) =>
      validateWebp(
        file,
        t.webpViewer.unsupportedBrowser,
        t.webpViewer.errorsText.too_large_image,
        36_000_000,
        undefined,
        undefined,
        signal,
      ),
    loadImage: (file, signal) =>
      loadWebpImage(
        file,
        {
          corrupt: t.webpViewer.errorsText.corrupt,
          decodeFailure: t.webpViewer.errorsText.decode_failure,
        },
        signal,
      ),
    renderActions: (context) => <WebpExportActions context={context} />,
    convertHref: '/webp-v-jpg-png',
    acceptedByEditors: true,
    relatedTools: t.webpViewer.relatedTools,
  }

  return <OpenImageViewerShell copy={t.webpViewer} config={config} />
}
